import React, { FocusEventHandler, ReactNode, useEffect, useState } from 'react';
import { Select } from 'antd';
import { SelectValue } from 'antd/lib/select';
import i18next from 'i18next';
import { useDebounce } from 'usehooks-ts';

import './index.less';

import { getDefaultIfUndefined } from '../../../utils/typeUtil';
import IbIcon from '../IbIcon';
import IbTypography from '../IbTypography';
import IbSpin from '../IbSpin';

const SEARCH_DELAY = 200; // ms
const OPTIONS_DEFAULT = [] as { value: string; label: ReactNode }[];
const STATUS_DEFAULT = 'default';
const SHOW_SEARCH_DEFAULT = false;

const MAIN_CLASS_NAME = 'ib-select';
const DROPDOWN_CLASS_NAME = `${MAIN_CLASS_NAME}__dropdown`;
const REMOVE_ICON_CLASS_NAME = `${MAIN_CLASS_NAME}__remove-icon`;

const CUSTOMIZED_SUFFIX_CLASS_NAME = `${MAIN_CLASS_NAME}_customized-suffix`;
const SUFFIX_CLASS_NAME = `${MAIN_CLASS_NAME}__suffix`;
const DEFAULT_SUFFIX_CLASS_NAME = `${SUFFIX_CLASS_NAME}__default`;
const CUSTOM_SUFFIX_CLASS_NAME = `${SUFFIX_CLASS_NAME}__custom`;

export interface IIbSelectProps {
  className?: string;
  dropdownClassName?: string;
  mode?: 'multiple' | 'tags';
  value?: SelectValue;
  options?: { value: string; label: ReactNode }[];
  status?: 'default' | 'success' | 'error';
  loading?: boolean;
  disabled?: boolean;
  placeholder?: string;
  showSearch?: boolean;
  allowClear?: boolean;
  notFoundContent?: ReactNode;
  suffix?: ReactNode;
  labelInValue?: boolean;
  filterOption?: boolean;
  onBlur?: FocusEventHandler<HTMLElement>;
  onChange?: (value: SelectValue) => void;
  onFocus?: FocusEventHandler<HTMLElement>;
  onSearch?: (searchValue: string) => void;
}

const IbSelect: React.FC<IIbSelectProps> = ({
  className,
  dropdownClassName,
  mode,
  value,
  options = OPTIONS_DEFAULT,
  status = STATUS_DEFAULT,
  loading = false,
  disabled,
  placeholder,
  showSearch = SHOW_SEARCH_DEFAULT,
  allowClear,
  notFoundContent,
  suffix,
  labelInValue,
  filterOption,
  onBlur,
  onChange,
  onFocus,
  onSearch,
}) => {
  options = getDefaultIfUndefined(options, OPTIONS_DEFAULT);
  status = getDefaultIfUndefined(status, STATUS_DEFAULT);
  showSearch = getDefaultIfUndefined(showSearch, SHOW_SEARCH_DEFAULT);

  const [searchValue, setSearchValue] = useState('');
  const [dropdownVisible, setDropdownVisible] = useState(false);

  const debouncedSearchValue = useDebounce(searchValue, SEARCH_DELAY);
  const deferredSearchValue = searchValue ? debouncedSearchValue : '';

  const classes = [MAIN_CLASS_NAME, `${MAIN_CLASS_NAME}_${status}`];
  className && classes.push(className);
  suffix && classes.push(CUSTOMIZED_SUFFIX_CLASS_NAME);

  const dropdownClasses = [DROPDOWN_CLASS_NAME];
  dropdownClassName && dropdownClasses.push(dropdownClassName);

  const onDeferredSearchValueChange = () => {
    onSearch?.(deferredSearchValue);
  };
  useEffect(onDeferredSearchValueChange, [deferredSearchValue]);

  const onDropdownVisibleChange = (value: boolean) => setDropdownVisible(value);

  const renderSuffix = () => {
    const defaultSuffix =
      loading && !dropdownVisible ? <IbSpin /> : <IbIcon iconName={showSearch ? 'search' : 'down'} size={20} />;

    if (suffix) {
      return (
        <div className={SUFFIX_CLASS_NAME}>
          <div className={DEFAULT_SUFFIX_CLASS_NAME}>{defaultSuffix}</div>
          <div className={CUSTOM_SUFFIX_CLASS_NAME}>{suffix}</div>
        </div>
      );
    }

    return defaultSuffix;
  };

  return (
    <Select
      allowClear={allowClear}
      className={classes.join(' ')}
      clearIcon={<IbIcon iconName="close" size={20} />}
      disabled={disabled}
      dropdownClassName={dropdownClasses.join(' ')}
      filterOption={filterOption ?? false}
      labelInValue={labelInValue ?? false}
      mode={mode}
      notFoundContent={
        notFoundContent ?? (
          <IbTypography.Paragraph disabled type="secondary">
            {i18next.t('No match found')}
          </IbTypography.Paragraph>
        )
      }
      options={options}
      placeholder={placeholder}
      removeIcon={<IbIcon className={REMOVE_ICON_CLASS_NAME} iconName="close-small" size={16} />}
      searchValue={searchValue}
      showSearch={showSearch}
      suffixIcon={renderSuffix()}
      value={value}
      virtual={false}
      onBlur={onBlur}
      onChange={onChange}
      onDropdownVisibleChange={onDropdownVisibleChange}
      onFocus={onFocus}
      onSearch={showSearch ? setSearchValue : undefined}
    />
  );
};

export default IbSelect;
